import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Space, Button, Typography, Alert, Spin, Input } from 'antd';
import {
  EditOutlined,
  SearchOutlined,
  LinkOutlined,
  PlusOutlined,
  BarChartOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { surveyService } from '../../services/survey.service';
import { Survey, SurveyStatus } from '../../types/survey.types';

const { Title } = Typography;

// Contractor Surveys List - View and manage surveys associated with the contractor

export const ContractorSurveys: React.FC = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    loadSurveys();
  }, []);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      setError(null);

      // Backend will automatically filter surveys based on authenticated user's contractorId
      const result = await surveyService.listSurveys({});
      setSurveys(result.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar pesquisas');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status: SurveyStatus) => {
    const statusMap: Record<SurveyStatus, { color: string; label: string }> = {
      [SurveyStatus.DRAFT]: { color: 'default', label: 'Rascunho' },
      [SurveyStatus.SCHEDULED]: { color: 'blue', label: 'Agendada' },
      [SurveyStatus.ACTIVE]: { color: 'success', label: 'Ativa' },
      [SurveyStatus.PAUSED]: { color: 'warning', label: 'Pausada' },
      [SurveyStatus.COMPLETED]: { color: 'purple', label: 'Concluída' },
      [SurveyStatus.CANCELED]: { color: 'error', label: 'Cancelada' },
    };
    return statusMap[status];
  };

  const filteredSurveys = surveys.filter((survey) =>
    survey.name.toLowerCase().includes(searchText.toLowerCase()) ||
    survey.description?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (text: string, record: Survey) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
              {record.description.substring(0, 100)}
              {record.description.length > 100 && '...'}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '120px',
      render: (status: SurveyStatus) => {
        const config = getStatusConfig(status);
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: 'Período',
      key: 'period',
      width: '180px',
      render: (_: any, record: Survey) => (
        <div style={{ fontSize: '13px' }}>
          <div>{new Date(record.startDate).toLocaleDateString('pt-BR')}</div>
          <div>{new Date(record.endDate).toLocaleDateString('pt-BR')}</div>
        </div>
      ),
    },
    {
      title: 'Abrangência',
      dataIndex: 'geographicScope',
      key: 'geographicScope',
      width: '120px',
      render: (scope: string) => {
        const scopeLabels: Record<string, string> = {
          NATIONAL: 'Nacional',
          STATE: 'Estadual',
          CITY: 'Municipal',
        };
        return <Tag>{scopeLabels[scope] || scope}</Tag>;
      },
    },
    {
      title: 'Questões',
      key: 'questions',
      width: '100px',
      align: 'center' as const,
      render: (_: any, record: Survey) => record.questions?.length || 0,
    },
    {
      title: 'Valor',
      dataIndex: 'surveyValue',
      key: 'surveyValue',
      width: '120px',
      render: (value: number | string | null | undefined) => {
        if (!value && value !== 0) return '-';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return isNaN(numValue) ? '-' : `R$ ${numValue.toFixed(2)}`;
      },
    },
    {
      title: 'Qtde Respostas',
      key: 'responseCount',
      width: '120px',
      align: 'center' as const,
      render: (_: any, record: Survey) => record._count?.responses || 0,
    },
    {
      title: 'Ações',
      key: 'actions',
      width: '320px',
      render: (_: any, record: Survey) => (
        <Space wrap>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/contractor/surveys/${record.id}/edit`)}
          >
            Editar
          </Button>
          <Button
            type="link"
            icon={<FileTextOutlined />}
            onClick={() => navigate(`/contractor/surveys/${record.id}/questionnaire-report`)}
          >
            Relatório do Questionário
          </Button>
          {record.status === SurveyStatus.COMPLETED && (
            <Button
              type="link"
              icon={<BarChartOutlined />}
              onClick={() => navigate(`/contractor/surveys/${record.id}/analysis`)}
            >
              Ver Análise
            </Button>
          )}
          {(record.status === SurveyStatus.ACTIVE || record.status === SurveyStatus.COMPLETED) &&
            record.publicAuditHash && (
              <Button
                type="link"
                icon={<LinkOutlined />}
                href={`/public/surveys/${record.publicAuditHash}/audit-trail`}
                target="_blank"
              >
                Trilha Completa
              </Button>
            )}
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Carregando pesquisas..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0 }}>
            Minhas Pesquisas
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/contractor/surveys/new')}
          >
            Nova Pesquisa
          </Button>
        </div>

        {error && (
          <Alert message="Erro" description={error} type="error" showIcon closable />
        )}

        <Input
          placeholder="Buscar pesquisas por nome ou descrição..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 400 }}
        />

        <Table
          columns={columns}
          dataSource={filteredSurveys}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} pesquisas`,
          }}
          locale={{
            emptyText: 'Nenhuma pesquisa encontrada',
          }}
        />
      </Space>
    </div>
  );
};
